/**
 * AppLifecycleHub — singleton that unifies web + native lifecycle events.
 *
 * Subscribes once to:
 *   - `document.visibilitychange` (web)
 *   - Capacitor `App.appStateChange` (native)
 *
 * Exposes a single typed event bus that consumers subscribe to.
 * This replaces v1's pattern of multiple hooks each adding their own
 * `visibilitychange` + `appStateChange` listeners independently.
 *
 * Usage:
 *   appLifecycleHub.on("foreground", () => { ... })
 *   appLifecycleHub.on("background", () => { ... })
 *   appLifecycleHub.on("stateChange", (active) => { ... })
 *
 * React integration: `useAppLifecycle()` hook auto-subscribes/unsubscribes.
 *
 * @see Fas 33 in view-and-dnd-tasks.md
 */

import { Capacitor, type PluginListenerHandle } from "@capacitor/core"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LifecycleEvent = "foreground" | "background" | "stateChange"
type ForegroundListener = () => void
type BackgroundListener = () => void
type StateChangeListener = (isActive: boolean) => void
type Listener = ForegroundListener | BackgroundListener | StateChangeListener

// ---------------------------------------------------------------------------
// Hub singleton
// ---------------------------------------------------------------------------

class AppLifecycleHubImpl {
  private listeners = new Map<LifecycleEvent, Set<Listener>>()
  private initialized = false
  private nativeListener: PluginListenerHandle | null = null

  /** Current app state */
  isActive = true

  constructor() {
    this.listeners.set("foreground", new Set())
    this.listeners.set("background", new Set())
    this.listeners.set("stateChange", new Set())
  }

  /**
   * Initialize listeners. Safe to call multiple times — only sets up once.
   * Should be called once during AppShell mount.
   */
  async init(): Promise<void> {
    if (this.initialized) return
    this.initialized = true

    // Web: visibilitychange
    document.addEventListener("visibilitychange", this.handleVisibilityChange)

    // Native: Capacitor app state
    if (Capacitor.isNativePlatform()) {
      try {
        const { App } = await import("@capacitor/app")
        this.nativeListener = await App.addListener(
          "appStateChange",
          (state) => {
            this.emit(state.isActive)
          },
        )
      } catch (e) {
        console.warn("[AppLifecycleHub] Failed to register native listener:", e)
      }
    }
  }

  /** Clean up all listeners. */
  destroy(): void {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    )
    this.nativeListener?.remove()
    this.nativeListener = null
    this.initialized = false
  }

  // ── Event subscription ──────────────────────────────────────────────

  on(event: "foreground", fn: ForegroundListener): () => void
  on(event: "background", fn: BackgroundListener): () => void
  on(event: "stateChange", fn: StateChangeListener): () => void
  on(event: LifecycleEvent, fn: Listener): () => void {
    this.listeners.get(event)!.add(fn)
    return () => {
      this.listeners.get(event)!.delete(fn)
    }
  }

  // ── Internal ────────────────────────────────────────────────────────

  private handleVisibilityChange = (): void => {
    const isActive = document.visibilityState === "visible"
    this.emit(isActive)
  }

  private emit(isActive: boolean): void {
    this.isActive = isActive

    // Notify stateChange listeners
    for (const fn of this.listeners.get("stateChange")!) {
      try {
        ;(fn as StateChangeListener)(isActive)
      } catch (e) {
        console.error("[AppLifecycleHub] stateChange listener error:", e)
      }
    }

    // Notify foreground/background listeners
    const event: LifecycleEvent = isActive ? "foreground" : "background"
    for (const fn of this.listeners.get(event)!) {
      try {
        ;(fn as ForegroundListener)()
      } catch (e) {
        console.error(`[AppLifecycleHub] ${event} listener error:`, e)
      }
    }
  }
}

/** Global singleton — import and use anywhere */
export const appLifecycleHub = new AppLifecycleHubImpl()
